import React, { useRef, useState, useEffect } from "react";
import Mousetrap from "mousetrap";
import Backend from "../../../../Backend/Backend";
import AddSymbol from "./AddSymbol/AddSymbol";
import { Dialog } from "primereact/dialog";
import useSize from "../../../../Utils/useSize/useSize";
import toast from "react-hot-toast";

import {
  FiPlus,
  FiRefreshCw,
  FiTrendingUp,
  FiDollarSign,
} from "react-icons/fi";

const backend = await new Backend().init();

export default function Header(props) {
  const userType = Number(localStorage.getItem("user_type") || 7);

  const server = localStorage.getItem("server") || "NoName";

  const windowSize = useSize();
  const searchRef = useRef(null);
  const fileInputRef = useRef(null);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    nse_future_active,
    nse_equity_active,
    mcx_future_active,
    mcx_options_active,
    nse_options_active,
    comex,
    crypto,
    forex,
    usstock,
    sgx,
    index,
    dgcx,
    ncdex,
  } = props;

  // ===============================
  // Keyboard Shortcut (SHIFT + S)
  // ===============================
  useEffect(() => {
    Mousetrap.bind("shift+s", () => {
      searchRef.current?.focus();
      return false;
    });

    return () => Mousetrap.unbind("shift+s");
  }, []);

  // ===============================
  // MODAL HANDLERS
  // ===============================
  const openSymbol = () => setOpenModal(true);

  const closeModal = () => {
    props.reload?.();
    setOpenModal(false);
  };

  // ===============================
  // CLOSING API
  // ===============================
  const ClosingSymbol = async () => {
    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
      };

      const r = await backend.closing_symbol(data);

      if (r.error === "False") {
        toast.success(r.message);
        props.reload?.();
      } else {
        toast.error(r.message);
      }
    } catch (err) {
      toast.error("Network error");
    }
  };

  // ===============================
  // UPDATE RATE API
  // ===============================
  const UpdateSymbolRate = () => {
    fileInputRef.current.click();
  };



  const handleFileUpload = async (e) => {
    if (loading) return; // 🔥 prevent multiple calls

    try {
      const file = e.target.files[0];
      if (!file) return;

      setLoading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", getTableName(props.type));

      formData.append("server", localStorage.getItem("server"));
      formData.append("id", localStorage.getItem("id"));
      formData.append("username", localStorage.getItem("username"));
      formData.append("token", localStorage.getItem("token"));

      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        type: getTableName(props.type),
      };

      const r = await backend.update_rate(data, formData);

      if (r.error === "False") {
        toast.success(r.message);
        props.reload?.();
      } else {
        toast.error(r.message);
      }

    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
      e.target.value = ""; // 🔥 VERY IMPORTANT (reset file input)
    }
  };

  const getTableName = (type) => {
    switch (type) {
      case 1: return "nse_future";
      case 2: return "nse_cash";
      case 3: return "mcx_future";
      case 4: return "mcx_options";
      case 5: return "fo";
      case 6: return "comex_future";
      case 7: return "crypto_future";
      case 8: return "forex_future";
      case 9: return "us_stock";
      case 10: return "sgx";
      case 11: return "index";
      case 12: return "dgcx";
      case 13: return "ncdex";
      default: return "symbol";
    }
  };

  const handleConfirm = () => setShowConfirm(true);

  const confirmClosing = () => {
    setShowConfirm(false);
    ClosingSymbol();
  };
  const cancelClosing = () => setShowConfirm(false);

  // ===============================
  // TAB BUTTON
  // ===============================
  const tabButton = (active, type, label) =>
    active === 1 && (
      <button
        key={type}
        onClick={() => props.updateTab(type)}
        className={`px-4 py-1 border-2 border-[var(--hover)] 
        whitespace-nowrap text-[12px] font-semibold rounded-md transition-all 
        ${props.type === type
            ? "bg-[var(--primary)] text-white shadow-md"
            : "bg-[var(--background)] text-[var(--text)]"
          }`}
      >
        {label}
      </button>
    );

  // ===============================
  // ACTION BUTTON (REUSABLE)
  // ===============================
  const ActionBtn = ({ icon: Icon, label, onClick, color }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1 
      font-semibold ${color} text-white 
      text-sm rounded-md transition hover:opacity-90 whitespace-nowrap`}
    >
      <Icon size={16} />
      {label}
    </button>
  );

  return (
    <div
      className="flex justify-between items-center py-2 px-2 
    bg-[var(--background)] border-b-2 border-[var(--hover)]"
    >
      {/* ================= LEFT TABS ================= */}
      <div
        className="flex gap-2 hide-scrollbar"
        style={{
          width: windowSize[0] / 2.5,
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        {tabButton(nse_future_active, 1, "NSE-FUTURE")}
        {tabButton(nse_equity_active, 2, "NSE-EQUITY")}
        {tabButton(mcx_future_active, 3, "MCX-FUTURE")}
        {tabButton(mcx_options_active, 4, "MCX-OPTIONS")}
        {tabButton(nse_options_active, 5, "NSE-OPTIONS")}
        {tabButton(comex, 6, "COMEX")}
        {tabButton(crypto, 7, "CRYPTO")}
        {tabButton(forex, 8, "FOREX")}
        {tabButton(usstock, 9, "US-STOCK")}
        {tabButton(sgx, 10, "SGX")}
        {tabButton(index, 11, "OTHERS")}
        {tabButton(dgcx, 12, "DGCX")}
        {tabButton(ncdex, 13, "NCDEX")}
      </div>

      {/* ================= RIGHT ACTIONS ================= */}
      <div className="flex items-center gap-3">
        <input
          ref={searchRef}
          type="text"
          placeholder="🔍 Search Symbol"
          className="px-3 py-1 w-[210px] text-sm rounded-md 
          bg-[var(--background)] border border-[var(--hover)] 
          focus:border-[var(--primary)] focus:outline-none 
          text-[var(--text)]"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            props.search?.(e.target.value);
          }}
        />

        {userType === 1 && (server === "ATMSTOX" || server === "SAUDAPRO") ? (
          <ActionBtn
            icon={FiPlus}
            label="ADD SYMBOL"
            color="bg-[var(--primary)]"
            onClick={openSymbol}
          />
        ) : null}

        <ActionBtn
          icon={FiTrendingUp}
          label="RUN CLOSING"
          color="bg-[var(--sell)]"
          onClick={handleConfirm}
        />

        <ActionBtn
          icon={FiRefreshCw}
          color={loading ? "bg-gray-400" : "bg-[var(--buy)]"}
          label={loading ? "UPLOADING..." : "UPDATE RATE"}
          onClick={!loading ? UpdateSymbolRate : null}
        />

        <input
          type="file"
          accept=".csv, .xlsx"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
      </div>

      {/* ================= ADD SYMBOL MODAL ================= */}
      <Dialog
        visible={openModal}
        onHide={() => setOpenModal(false)}
        position="center"
        focusOnShow={false}
        transitionOptions={false}
      >
        <AddSymbol close={closeModal} />
      </Dialog>

      {/* ================= CONFIRM MODAL ================= */}
      {showConfirm && (
        <div className="logoutModalOverlay">
          <div className="logoutModal">
            <h3 className="logoutTitle">Confirm Action</h3>
            <p className="logoutText">Are you sure you want to proceed?</p>

            <div className="logoutModalButtons">
              <button className="confirmBtn" onClick={confirmClosing}>
                Yes, Proceed
              </button>
              <button className="cancelBtn" onClick={cancelClosing}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
